<template>
  <div id="block-level">
    <Take5DailyLayout>
      <div class="lg:col-span-3">
        <SearchNav fallbackUrl="/take-5-daily" />
        <hr class="border-t border-t-gray-300 absolute left-0 w-full md:w-[80vw]" aria-hidden="true" />
        <template v-if="!details">
          <div class="flex items-center justify-center py-20">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </template>
        <template v-else>
          <div>
            <BlogDetail :details="details" :mediaType="slug" />

            <KeepReading v-if="slug === 'article' && keepReadingTags.length" title="Keep Reading"
              :tags="keepReadingTags" />

            <Transcript v-for="(item, i) in transcriptBlocks" :key="i" :heading="item.heading"
              :description="item.description" :id="`trans-${i}`" />
          </div>
          <div v-if="slug === 'podcast'">
            <div v-if="loading"
              class="border-r-0 md:border-r md:border-gray-300 lg:col-span-3 flex items-center justify-center py-20">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <section v-else class="lg:py-[4rem] lg:pr-[2.142857rem] py-[2rem] px-6 lg:pl-0">
              <Heading :level="5" size="xl" class="pb-[1.071428rem] !text-ash-200">
                More Podcasts
              </Heading>

              <MediaGrid class="lg:col-span-3 relative" :tertiary="morePodcast" titlePosition="bottom"
                :showSearch="true" :isShowTertiarydataonly="true" />
            </section>
          </div>
        </template>
      </div>
    </Take5DailyLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect } from "vue";
import { useRoute } from "vue-router";

import Take5DailyLayout from "~/layouts/take5daily.vue";
import SearchNav from "~/components/ui/organism/take-five-daily/searchNav.vue";
import BlogDetail from "~/components/ui/organism/take-five-daily/mediaDetail.vue";
import Transcript from "~/components/ui/molecule/transcript.vue";
import KeepReading from "~/components/ui/atom/keepReading.vue";
import MediaGrid from "~/components/ui/organism/take-five-daily/mediaGrid.vue";
import Heading from "~/components/ui/atom/heading.vue";

import { useTake5DailyDetails, useMorePodcast } from "~/composables/blogDetails";
import {
  blogDetailsValue,
  type ParsedBlog,
  type BlogType,
} from "~/components/utils//blogDetailsValue";
import { extractTextFromHtml } from "#shared/utils/common";
import { CONTENT_TYPES } from "~~/shared/utils/contentTypes";

const route = useRoute()
const details = ref<ParsedBlog | null>(null); 

const slugTitle = route.params.slug
const lastSlug = Array.isArray(slugTitle) ? slugTitle[slugTitle.length - 1] : slugTitle
const formattedTitle = lastSlug!.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
const pageTitle = computed(() => {
  const builderTitle = details.value?.metaTitle?.trim() || details.value?.title?.trim();
  return `${builderTitle || formattedTitle} | Thorne`;
})

useHead({
  title: pageTitle
})

definePageMeta({
  layout: 'take-5-daily' as any
})

const normalizeEntityType = (entityType?: string | null): BlogType | null => {
  const normalizedType = (entityType || '').trim().toLowerCase();

  if (
    normalizedType === CONTENT_TYPES.ARTICLE ||
    normalizedType === CONTENT_TYPES.ARTICLES
  ) return 'article';

  if (
    normalizedType === CONTENT_TYPES.VIDEO ||
    normalizedType === CONTENT_TYPES.VIDEOS
  ) return 'video';

  if (
    normalizedType === CONTENT_TYPES.PODCAST ||
    normalizedType === CONTENT_TYPES.PODCASTS
  ) return 'podcast';

  return null;
};

const slug = computed<BlogType>(
  () => (route.params?.slug?.[0] as BlogType) ?? "article"
);

const slugUrlKey = computed(() => route.params?.slug?.[1] ?? "");

if (!slugUrlKey.value) {
  showError({ statusCode: 404, statusMessage: "Page Not Found" });
}

const { blogDetails: data, pending } = useTake5DailyDetails(slugUrlKey.value);
const blogDetails = ref<any>(null);

watchEffect(() => {
  blogDetails.value = data.value;
});


watchEffect(() => {
  if (!pending?.value && !data.value) {
    showError({
      statusCode: 404,
      statusMessage: "Page Not Found"
    });
  }
  if (!pending?.value && blogDetails?.value) {
    const entityType = normalizeEntityType(blogDetails.value.entityType)

    if (!entityType || entityType !== slug.value) {
      showError({
        statusCode: 404,
        statusMessage: "Page Not Found"
      })
      return
    }
    details.value = blogDetailsValue(blogDetails.value, slug.value);
  }
});

const tags = computed(
  () => blogDetails.value?.taggings?.map((t) => t.tag) ?? []
);

const currentId = computed(() => blogDetails.value?.takeFiveId ?? null);

const { morePodcast, loading } = useMorePodcast(tags, currentId);

const keepReadingTags = computed(() => {
  return details.value?.tags ?? [];
});

const isTranscript = computed(
  () => slug.value === "podcast" || slug.value === "video"
);

const transcriptBlocks = computed(() => {
  if (!isTranscript.value || !details.value) return [];

  const { rundown, rundownTitle, transcript } = details.value;

  const blocks: { heading: string; description: string }[] = [];

  if (rundown && rundownTitle && !isHtmlContentEmpty(rundown)) {
    blocks.push({
      heading: rundownTitle,
      description: rundown,
    });
  }

  if (transcript && !isHtmlContentEmpty(transcript)) {
    blocks.push({
      heading: "Transcript",
      description: transcript,
    });
  }

  return blocks;
});

const isHtmlContentEmpty = (html: string) => {
  const text = extractTextFromHtml(html ?? "");
  return text.length === 0;
}
</script>
